import "@livekit/components-styles";

import {
  LiveKitRoom,
  RoomAudioRenderer,
} from "@livekit/components-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { useCollabAnnotatorStore } from "@/lib/collabAnnotator";
import { useConsentStore } from "@/lib/consent";
import { FIELD_VIDEO_DEFAULTS, ROOM_OPTIONS } from "@/lib/livekitOptions";
import { useRecordingStore } from "@/lib/recording";
import { useWakeLock } from "@/lib/wakeLock";
import { InRoomAnnotation } from "@/modules/annotation";
import { FieldChatDrawer } from "@/modules/chat";
import { EvidenceAnnotator } from "@/modules/evidence";
import { WhiteboardAutoOpen } from "@/modules/whiteboard";
import type { SessionStatus } from "@/lib/types";

import { ConsentHud } from "./ConsentHud";
import { FieldCaptureHandler } from "./FieldCaptureHandler";
import { FieldCommandsHandler } from "./FieldCommandsHandler";
import { FieldLayout } from "./FieldLayout";
import { FieldSystemHandler, useSessionEndedStore } from "./FieldSystemHandler";

type Props = {
  token: string;
  url: string;
  sessionId: string;
  fieldSessionToken: string;
  /** Phase 1B-J — server-anchored REC state, surfaced via the join
   *  response so the badge renders on connect without depending on a
   *  data-channel envelope. `null` means recording isn't running. */
  sessionStatus?: SessionStatus;
  recordingStartedAt?: string | null;
};

export function FieldRoom({
  token,
  url,
  sessionId,
  fieldSessionToken,
  sessionStatus,
  recordingStartedAt,
}: Props) {
  const { t } = useTranslation();
  const ended = useSessionEndedStore((s) => s.ended);
  const reset = useSessionEndedStore((s) => s.reset);
  const collabEvidenceId = useCollabAnnotatorStore((s) => s.openEvidenceId);
  const setConsent = useConsentStore((s) => s.set);
  const setRecording = useRecordingStore((s) => s.set);

  // Phase 1B-J — bootstrap the REC badge state from the server-side
  // anchor as soon as the join response lands. Idempotent: when the
  // operator's data-channel envelope arrives later it just re-applies
  // the same values. The recording.started SOUND still only plays in
  // response to the envelope (a transition cue, not a "currently
  // running" cue), so a late-joiner who connects mid-recording sees
  // the badge but doesn't hear the start chime — that's the right UX.
  useEffect(() => {
    if (
      recordingStartedAt &&
      (sessionStatus === "recording" || sessionStatus === "paused")
    ) {
      const startMs = new Date(recordingStartedAt).getTime();
      setConsent("recording", "granted");
      setRecording(true, startMs);
      if (sessionStatus === "paused") {
        useRecordingStore.getState().pause(Date.now());
      }
    }
  }, [recordingStartedAt, sessionStatus, setConsent, setRecording]);

  // Keep the screen awake for the duration of the field session — iOS
  // Safari 16.4+ Wake Lock API. No-op on older browsers. Released on
  // unmount + on visibility-hidden + re-acquired on visible.
  useWakeLock(!ended);

  // Reset the flag whenever this component mounts (re-using the URL to
  // join a different session shouldn't surface the previous session's
  // end-screen).
  useEffect(() => {
    reset();
    return () => reset();
  }, [sessionId, reset]);

  if (ended) {
    return (
      <main className="flex h-dvh items-center justify-center bg-nb-bg p-6 text-nb-text">
        <div className="card max-w-md text-center">
          <h1 className="text-2xl font-semibold">{t("ui.fieldRoom.sessionEnded")}</h1>
          <p className="mt-2 text-sm text-nb-text-2">
            {t("ui.fieldRoom.sessionEndedDescription")}
          </p>
        </div>
      </main>
    );
  }

  return (
    <div className="relative h-full">
      <ConsentHud />
      <LiveKitRoom
        token={token}
        serverUrl={url}
        connect
        // Default to the back camera AT 1080p explicitly.
        //
        // Two reasons the resolution MUST be inline here:
        //   1. Defaults: without `facingMode` the browser usually picks
        //      the front camera on first acquire, forcing every field
        //      user to hit "switch camera" right after joining.
        //   2. Resolution: when `video` is passed as an OBJECT,
        //      LiveKit's `videoCaptureDefaults.resolution` from
        //      ROOM_OPTIONS does NOT merge in — the object overrides
        //      defaults entirely. Without explicit resolution iPhone
        //      Safari falls back to its own back-camera default which
        //      is 480×640 portrait (confirmed via getSettings() on
        //      2026-05-17). The egress then upscales 480×640 to 1080p
        //      → "blurry HD" file: real resolution low, encoded
        //      resolution high, perceived quality terrible.
        //
        // `facingMode` is a soft preference (browsers ignore it on
        // single-camera devices); FieldCommandsHandler's
        // `desiredFacing` tracking handles later restarts that
        // re-acquire at the wrong facing.
        video={FIELD_VIDEO_DEFAULTS}
        audio
        options={ROOM_OPTIONS}
        data-lk-theme="default"
        className="h-full"
      >
        <FieldLayout sessionId={sessionId} fieldSessionToken={fieldSessionToken} />
        <RoomAudioRenderer />
        <InRoomAnnotation
          sessionId={sessionId}
          editable={false}
          fieldSessionToken={fieldSessionToken}
        >
          {/* Field-side handlers: capture command, camera/torch commands,
              and system envelopes (recording / session.ended). All live
              inside the bus context. */}
          <FieldCaptureHandler
            sessionId={sessionId}
            fieldSessionToken={fieldSessionToken}
          />
          <FieldCommandsHandler sessionId={sessionId} />
          <FieldSystemHandler sessionId={sessionId} />
          <WhiteboardAutoOpen sessionId={sessionId} />
          {collabEvidenceId && (
            <EvidenceAnnotator evidenceId={collabEvidenceId} side="field" />
          )}
          {/* Chat drawer sits inside InRoomAnnotation so it sees the
              populated useSessionBus bus and can broadcast chat/message
              envelopes when the field user sends a line. The EvidenceAnnotator
              overlays cover the chat button when active (z-index higher) —
              field can't reach chat while collab-annotating, which is the
              intended focus. */}
          <FieldChatDrawer
            sessionId={sessionId}
            fieldSessionToken={fieldSessionToken}
          />
        </InRoomAnnotation>
      </LiveKitRoom>
    </div>
  );
}